import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ChevronLeft, ChevronRight, Download, Edit3, FileText, LogOut, Plus, RefreshCw, Rows3, Search, Trash2, Upload, XCircle } from 'lucide-react';
import lbIcon from './components/favicon.png';
import { useAuth } from './AuthContext';


const newKey = (prefix) => `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const watchLabel = watch => {
  if (watch.connector_mode === 'generic_web_llm' || watch.connector === 'generic_web_llm') {
    return `General URL watch · ${watch.hostname || 'external source'}`;
  }
  try {
    const parsed = new URL(watch.normalized_url);
    const location = new URLSearchParams(parsed.hash.replace(/^#/, '')).get('f:location');
    return location ? `Colliers · ${location}` : 'Colliers land listings';
  } catch (_error) {
    return 'Colliers land listings';
  }
};

const requestJson = async (url, options = {}) => {
  const response = await fetch(url, { credentials: 'include', ...options });
  let payload = {};
  try { payload = await response.json(); } catch (_error) { payload = {}; }
  if (!response.ok) throw new Error(payload.error || `Request failed (${response.status})`);
  return payload;
};

const PortfolioPage = () => {
  const { authData } = useAuth();
  const [context, setContext] = useState(authData);
  const [portfolios, setPortfolios] = useState([]);
  const [activeId, setActiveId] = useState('');
  const [rows, setRows] = useState([]);
  const [watches, setWatches] = useState([]);
  const [watchEnabled, setWatchEnabled] = useState(Boolean(authData?.portfolio_watch_enabled));
  const [urlWatchUiEnabled, setUrlWatchUiEnabled] = useState(authData?.portfolio_url_watch_ui_enabled !== false);
  const [watchUrl, setWatchUrl] = useState('');
  const [watchPermission, setWatchPermission] = useState(false);
  const [manualPullEnabled, setManualPullEnabled] = useState(Boolean(authData?.portfolio_manual_pull_enabled));
  const [manualPullUrl, setManualPullUrl] = useState('');
  const [manualPullPermission, setManualPullPermission] = useState(false);
  const [manualPullOpen, setManualPullOpen] = useState(false);
  const [manualPullSession, setManualPullSession] = useState(null);
  const [manualPullToken, setManualPullToken] = useState('');
  const [manualPullViewUrl, setManualPullViewUrl] = useState('');
  const [manualPullStreamFrame, setManualPullStreamFrame] = useState('');
  const [manualPullStreamState, setManualPullStreamState] = useState('connecting');
  const [manualPullCursor, setManualPullCursor] = useState(null);
  const [manualPullMessage, setManualPullMessage] = useState('');
  const [manualPullReview, setManualPullReview] = useState(null);
  const [manualPullComplete, setManualPullComplete] = useState(false);
  const [manualPullSelected, setManualPullSelected] = useState(new Set());
  const [manualPullJob, setManualPullJob] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [editRowIdentity, setEditRowIdentity] = useState({ address: '', apn: '', latitude: '', longitude: '' });
  const [editingWatchId, setEditingWatchId] = useState('');
  const [editWatchUrl, setEditWatchUrl] = useState('');
  const [editWatchPermission, setEditWatchPermission] = useState(false);
  const [archivedListings, setArchivedListings] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [originFilter, setOriginFilter] = useState('all');
  const [availability, setAvailability] = useState({});
  const [reportJobs, setReportJobs] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [portfolioName, setPortfolioName] = useState('');
  const [manual, setManual] = useState({ address: '', apn: '', latitude: '', longitude: '' });
  const [csvFile, setCsvFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [csvInputVersion, setCsvInputVersion] = useState(0);
  const [filter, setFilter] = useState('');
  const [sort, setSort] = useState({ field: 'updated_at', direction: 'desc' });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [deletedPortfolio, setDeletedPortfolio] = useState(null);
  const [deletedRow, setDeletedRow] = useState(null);
  const rowRequestRef = useRef(0);
  const importRequestRef = useRef(0);
  const importIdentityRef = useRef(null);
  const reportIdentityRef = useRef(null);
  const manualPullViewRef = useRef('');
  const manualPullStreamRef = useRef(null);
  const manualPullStreamTicketRef = useRef('');
  const manualPullStreamMoveRef = useRef(0);
  const manualPullJobImportRef = useRef('');
  // Browser input must be delivered in order.  In particular, a pointer
  // release must never overtake its preceding press or wait behind a backlog
  // of hover events.
  const manualPullInputQueueRef = useRef(Promise.resolve());

  const organizationId = context?.portfolio_organization_id;
  const activePortfolio = portfolios.find(item => item.portfolio_id === activeId);
  const baseUrl = organizationId ? `/api/portfolio/organizations/${encodeURIComponent(organizationId)}` : '';

  const loadPortfolios = async (preferredId = '', preserveCurrent = true) => {
    if (!organizationId) return;
    const data = await requestJson(`${baseUrl}/portfolios`);
    const nextPortfolios = data.portfolios || [];
    setPortfolios(nextPortfolios);
    const candidates = [preferredId, preserveCurrent ? activeId : '', nextPortfolios[0]?.portfolio_id];
    const nextId = candidates.find(candidate => candidate && nextPortfolios.some(item => item.portfolio_id === candidate)) || '';
    setActiveId(nextId);
    return nextPortfolios;
  };

  const loadRows = async () => {
    const requestId = ++rowRequestRef.current;
    if (!organizationId || !activeId) {
      setRows([]);
      setSelected(new Set());
      return;
    }
    try {
      const data = await requestJson(`${baseUrl}/portfolios/${encodeURIComponent(activeId)}/rows`);
      if (requestId !== rowRequestRef.current) return;
      setRows(data.rows || []);
      setSelected(new Set());
      setError('');
    } catch (err) {
      if (requestId === rowRequestRef.current) setError(err.message);
    }
  };

  const loadWatches = async () => {
    if (!organizationId || !activeId) { setWatches([]); setWatchEnabled(false); return; }
    try {
      const data = await requestJson(`${baseUrl}/portfolios/${encodeURIComponent(activeId)}/watches`);
      setWatches(data.watches || []);
      setWatchEnabled(Boolean(data.watch_enabled));
    } catch (_error) {
      // Watch status is additive; source failure must not disable Portfolio CRUD.
      setWatches([]);
      setWatchEnabled(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const nextContext = authData || await requestJson('/auth/current-user');
        if (cancelled) return;
        setContext(nextContext);
        setUrlWatchUiEnabled(nextContext?.portfolio_url_watch_ui_enabled !== false);
        setManualPullEnabled(Boolean(nextContext?.portfolio_manual_pull_enabled));
        if (!nextContext?.portfolio_enabled || !nextContext?.portfolio_organization_id) return;
        const data = await requestJson(`/api/portfolio/organizations/${encodeURIComponent(nextContext.portfolio_organization_id)}/portfolios`);
        if (cancelled) return;
        setPortfolios(data.portfolios || []);
        setActiveId(data.portfolios?.[0]?.portfolio_id || '');
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [authData]);

  useEffect(() => {
    setPage(1);
    setCsvFile(null);
    setImportResult(null);
    setSaving(false);
    setCsvInputVersion(value => value + 1);
    importIdentityRef.current = null;
    importRequestRef.current += 1;
    setWatches([]);
    setArchivedListings([]);
    setShowArchived(false);
    setOriginFilter('all');
    loadRows();
    loadWatches();
    // activeId is the durable server selection target; filters remain view-local.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  useEffect(() => () => {
    if (manualPullViewRef.current) URL.revokeObjectURL(manualPullViewRef.current);
    if (manualPullStreamRef.current) manualPullStreamRef.current.close();
  }, []);

  const manualPullBase = manualPullSession && activeId
    ? `${baseUrl}/portfolios/${encodeURIComponent(activeId)}/manual-pull/sessions/${encodeURIComponent(manualPullSession.session_id)}`
    : '';

  const refreshManualPullView = async (session = manualPullSession, token = manualPullToken) => {
    if (!session || !token || !activeId) return;
    if (manualPullStreamState === 'connected') return;
    const response = await fetch(`${baseUrl}/portfolios/${encodeURIComponent(activeId)}/manual-pull/sessions/${encodeURIComponent(session.session_id)}/view`, {
      credentials: 'include', headers: { 'X-Portfolio-Session-Token': token },
    });
    if (!response.ok) return;
    const blob = await response.blob();
    const nextUrl = URL.createObjectURL(blob);
    if (manualPullViewRef.current) URL.revokeObjectURL(manualPullViewRef.current);
    manualPullViewRef.current = nextUrl;
    setManualPullViewUrl(nextUrl);
  };

  useEffect(() => {
    if (!manualPullSession || !manualPullToken || !manualPullOpen || !manualPullBase || manualPullStreamState === 'connected') return undefined;
    let cancelled = false;
    let timer = null;
    const poll = async () => {
      try {
        const data = await requestJson(manualPullBase, { headers: { 'X-Portfolio-Session-Token': manualPullToken } });
        if (cancelled) return;
        setManualPullSession(data.session);
        await refreshManualPullView(data.session, manualPullToken);
      } catch (err) {
        if (!cancelled) {
          if (/manual_pull_session_(?:not_found|expired|not_active)/i.test(err.message || '')) {
            if (manualPullViewRef.current) URL.revokeObjectURL(manualPullViewRef.current);
            manualPullViewRef.current = '';
            setManualPullViewUrl('');
            setManualPullSession(null);
            setManualPullToken('');
            setManualPullMessage('This browser session is no longer active. Reopen the source to continue.');
          } else setManualPullMessage(err.message);
        }
      }
      if (!cancelled) timer = window.setTimeout(poll, 2500);
    };
    poll();
    return () => { cancelled = true; if (timer) window.clearTimeout(timer); };
    // The session token is intentionally stable for this modal lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualPullOpen, manualPullSession?.session_id, manualPullToken, manualPullBase, manualPullStreamState]);

  useEffect(() => {
    if (!organizationId || !activeId) {
      setAvailability({});
      return undefined;
    }
    let cancelled = false;
    let timer = null;
    const refresh = async (enqueue = false) => {
      try {
        if (enqueue) await requestJson(`${baseUrl}/portfolios/${encodeURIComponent(activeId)}/availability-checks`, { method: 'POST' });
        const data = await requestJson(`${baseUrl}/portfolios/${encodeURIComponent(activeId)}/availability-checks`);
        if (cancelled) return;
        const grouped = {};
        (data.checks || []).forEach(check => {
          grouped[check.row_id] = grouped[check.row_id] || {};
          // The API returns newest checks first. Keep the latest provider version
          // instead of overwriting it with an older cached check for the same type.
          if (!grouped[check.row_id][check.data_type]) grouped[check.row_id][check.data_type] = check;
        });
        setAvailability(grouped);
        if ((data.metrics?.active_depth || 0) > 0) timer = window.setTimeout(() => refresh(false), 3000);
      } catch (_error) {
        // Availability is an independent enhancement; portfolio CRUD remains usable.
        if (!cancelled) timer = window.setTimeout(() => refresh(false), 10000);
      }
    };
    refresh(true);
    return () => { cancelled = true; if (timer) window.clearTimeout(timer); };
  }, [organizationId, activeId, baseUrl, rows]);

  const refreshReportJobs = async () => {
    if (!organizationId || !activeId) return;
    const data = await requestJson(`${baseUrl}/portfolios/${encodeURIComponent(activeId)}/report-jobs`);
    const grouped = {};
    (data.jobs || []).forEach(job => {
      if (!grouped[job.row_id] || String(job.updated_at || '') > String(grouped[job.row_id].updated_at || '')) grouped[job.row_id] = job;
    });
    setReportJobs(grouped);
  };

  useEffect(() => {
    if (!organizationId || !activeId) { setReportJobs({}); return undefined; }
    let cancelled = false;
    let timer = null;
    const poll = async () => {
      try { if (!cancelled) await refreshReportJobs(); } catch (_error) { /* report polling must not disable portfolio CRUD */ }
      if (!cancelled) {
        timer = window.setTimeout(poll, 5000);
      }
    };
    poll();
    return () => { cancelled = true; if (timer) window.clearTimeout(timer); };
    // Poll identity follows the selected durable portfolio.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, activeId, baseUrl]);

  useEffect(() => {
    if (!manualPullJob?.manual_pull_job_id || !organizationId || !activeId || !manualPullToken || !manualPullSession) return undefined;
    let cancelled = false;
    let timer = null;
    const poll = async () => {
      try {
        const data = await requestJson(`${baseUrl}/portfolios/${encodeURIComponent(activeId)}/manual-pull/jobs/${encodeURIComponent(manualPullJob.manual_pull_job_id)}`);
        if (cancelled) return;
        const job = data.job || {};
        setManualPullJob(job);
        const terminal = ['completed', 'partial', 'failed', 'cancelled', 'expired'].includes(job.status);
        if (terminal && ['completed', 'partial'].includes(job.status) && job.review_id && job.candidate_ids?.length && manualPullJobImportRef.current !== job.manual_pull_job_id) {
          manualPullJobImportRef.current = job.manual_pull_job_id;
          const complete = Boolean(job.status === 'completed' && job.archival_eligible);
          await requestJson(`${baseUrl}/portfolios/${encodeURIComponent(activeId)}/manual-pull/sessions/${encodeURIComponent(manualPullSession.session_id)}/import`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Portfolio-Session-Token': manualPullToken, 'Idempotency-Key': newKey('manual-pull-background-import') },
            body: JSON.stringify({ review_id: job.review_id, candidate_ids: job.candidate_ids, complete_inventory_confirmed: complete }),
          });
          await Promise.all([loadRows(), loadWatches()]);
          setNotice(`Background pull finished: ${job.candidate_count || 0} listings added; ${job.review_count || 0} need review.`);
          setManualPullSession(null); setManualPullToken(''); setManualPullJob(null);
        } else if (terminal) {
          setNotice(job.status === 'failed' ? 'Background listing pull failed; no rows were changed.' : `Background listing pull ${job.status}.`);
          setManualPullSession(null); setManualPullToken(''); setManualPullJob(null);
        }
        if (!terminal && !cancelled) timer = window.setTimeout(poll, 2000);
      } catch (err) {
        if (!cancelled) { setError(err.message); timer = window.setTimeout(poll, 5000); }
      }
    };
    poll();
    return () => { cancelled = true; if (timer) window.clearTimeout(timer); };
    // The job and session token are intentionally stable for this background run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualPullJob?.manual_pull_job_id, organizationId, activeId, manualPullToken, manualPullSession?.session_id]);

  const visibleRows = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    const filtered = rows.filter(row => {
      const matchesText = !needle || JSON.stringify(row.normalized_input || {}).toLowerCase().includes(needle);
      const matchesOrigin = originFilter === 'all' || (row.origins || []).some(item => item.origin === originFilter);
      return matchesText && matchesOrigin;
    });
    return [...filtered].sort((left, right) => {
      const leftValue = sort.field === 'identity'
        ? (left.normalized_input?.address || left.normalized_input?.apn || '')
        : (left[sort.field] || '');
      const rightValue = sort.field === 'identity'
        ? (right.normalized_input?.address || right.normalized_input?.apn || '')
        : (right[sort.field] || '');
      return String(leftValue).localeCompare(String(rightValue)) * (sort.direction === 'asc' ? 1 : -1);
    });
  }, [rows, filter, originFilter, sort]);

  const pageCount = Math.max(1, Math.ceil(visibleRows.length / pageSize));
  const pagedRows = visibleRows.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    setPage(current => Math.min(current, pageCount));
    setSelected(current => new Set([...current].filter(rowId => rows.some(row => row.row_id === rowId))));
  }, [pageCount, rows]);

  const toggleSort = field => setSort(previous => ({ field, direction: previous.field === field && previous.direction === 'asc' ? 'desc' : 'asc' }));
  const toggleRow = rowId => setSelected(previous => {
    const next = new Set(previous);
    if (next.has(rowId)) next.delete(rowId); else next.add(rowId);
    return next;
  });
  const togglePage = () => setSelected(previous => {
    const next = new Set(previous);
    const eligibleRows = pagedRows.filter(rowEligible);
    const allSelected = eligibleRows.length > 0 && eligibleRows.every(row => next.has(row.row_id));
    eligibleRows.forEach(row => allSelected ? next.delete(row.row_id) : next.add(row.row_id));
    return next;
  });
  const rowEligible = row => row.identity_validation_state !== 'needs_review'
    && row.identity_validation_state !== 'unresolved'
    && availability[row.row_id]?.parcel?.state === 'available';
  const selectAllFiltered = () => setSelected(new Set(visibleRows.filter(rowEligible).map(row => row.row_id)));
  const selectedScope = () => {
    const selectedIds = [...selected];
    const filteredIds = visibleRows.filter(rowEligible).map(row => row.row_id);
    const pageIds = pagedRows.filter(rowEligible).map(row => row.row_id);
    if (filteredIds.length && selectedIds.length === filteredIds.length && filteredIds.every(rowId => selected.has(rowId))) return 'filtered';
    if (pageIds.length && selectedIds.length === pageIds.length && pageIds.every(rowId => selected.has(rowId))) return 'visible';
    return 'selected';
  };

  const createPortfolio = async event => {
    event.preventDefault();
    if (!portfolioName.trim()) return setError('Enter a portfolio name.');
    setSaving(true); setError(''); setNotice('');
    try {
      const data = await requestJson(`${baseUrl}/portfolios`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': newKey('portfolio') },
        body: JSON.stringify({ name: portfolioName }),
      });
      setPortfolioName('');
      setDeletedPortfolio(null);
      await loadPortfolios(data.portfolio.portfolio_id);
      setNotice(`Portfolio “${data.portfolio.name}” created.`);
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const renamePortfolio = async () => {
    const name = window.prompt('Rename portfolio', activePortfolio?.name || '');
    if (!name || name.trim() === activePortfolio?.name) return;
    setSaving(true); setError(''); setNotice('');
    try {
      await requestJson(`${baseUrl}/portfolios/${encodeURIComponent(activeId)}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': newKey('rename') }, body: JSON.stringify({ name }),
      });
      await loadPortfolios(activeId);
      setDeletedPortfolio(null);
      setNotice(`Portfolio renamed to “${name.trim()}”.`);
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const deletePortfolio = async () => {
    if (!activePortfolio || !window.confirm(`Delete “${activePortfolio.name}”? It will remain recoverable for 30 days.`)) return;
    setSaving(true); setError(''); setNotice('');
    try {
      const deleted = { portfolioId: activeId, name: activePortfolio.name };
      await requestJson(`${baseUrl}/portfolios/${encodeURIComponent(activeId)}`, { method: 'DELETE' });
      await loadPortfolios('', false);
      setDeletedPortfolio(deleted);
      setDeletedRow(null);
      setNotice(`Portfolio “${activePortfolio.name}” deleted.`);
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const restorePortfolio = async () => {
    if (!deletedPortfolio) return;
    setSaving(true); setError('');
    try {
      const data = await requestJson(`${baseUrl}/portfolios/${encodeURIComponent(deletedPortfolio.portfolioId)}/restore`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': newKey('restore-portfolio') }, body: '{}',
      });
      await loadPortfolios(data.portfolio.portfolio_id, false);
      setNotice(`Portfolio “${data.portfolio.name}” restored.`);
      setDeletedPortfolio(null);
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const addManualRow = async event => {
    event.preventDefault();
    if (!activeId) return setError('Create or select a portfolio first.');
    setSaving(true); setError(''); setNotice('');
    try {
      await requestJson(`${baseUrl}/portfolios/${encodeURIComponent(activeId)}/rows`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': newKey('manual-row') }, body: JSON.stringify(manual),
      });
      setManual({ address: '', apn: '', latitude: '', longitude: '' });
      await loadRows();
      setNotice('Parcel added to the portfolio.');
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const importCsv = async event => {
    event.preventDefault();
    if (!activeId) return setError('Create or select a portfolio first.');
    if (!csvFile) return setError('Choose a CSV file to import.');
    const portfolioAtStart = activeId;
    const requestId = ++importRequestRef.current;
    const signature = `${activeId}:${csvFile.name}:${csvFile.size}:${csvFile.lastModified}`;
    if (!importIdentityRef.current || importIdentityRef.current.signature !== signature) {
      importIdentityRef.current = { signature, key: newKey('csv-import') };
    }
    const idempotencyKey = importIdentityRef.current.key;
    setSaving(true); setError(''); setNotice(''); setImportResult(null);
    try {
      const form = new FormData();
      form.append('file', csvFile);
      const data = await requestJson(`${baseUrl}/portfolios/${encodeURIComponent(activeId)}/imports`, {
        method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: form,
      });
      if (requestId !== importRequestRef.current || portfolioAtStart !== activeId) return;
      setImportResult(data.batch);
      await loadRows();
      setNotice(`CSV import completed: ${data.batch.accepted_count} accepted, ${data.batch.rejected_count} rejected.`);
      setCsvFile(null);
      importIdentityRef.current = null;
      event.target.reset();
    } catch (err) {
      if (requestId === importRequestRef.current) setError(err.message);
    } finally {
      if (requestId === importRequestRef.current) setSaving(false);
    }
  };

  const createWatch = async event => {
    event.preventDefault();
    if (!activeId) return setError('Create or select a portfolio first.');
    if (!watchUrl.trim()) return setError('Enter a listings URL.');
    setSaving(true); setError(''); setNotice('');
    try {
      const data = await requestJson(`${baseUrl}/portfolios/${encodeURIComponent(activeId)}/watches`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Idempotency-Key': newKey('watch') },
        body: JSON.stringify(watchPermission
          ? { source_url: watchUrl, connector: 'generic_web_llm', connector_mode: 'generic_web_llm', permission_attested: true }
          : { source_url: watchUrl, connector: 'colliers_tampa' }),
      });
      setWatchUrl('');
      setWatchPermission(false);
      await loadWatches();
      setNotice(data.created ? (watchPermission ? 'General URL watch created.' : 'Colliers listings watch created.') : 'This listings watch already exists.');
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const beginWatchEdit = watch => {
    setEditingWatchId(watch.watch_source_id);
    setEditWatchUrl(watch.source_url || watch.normalized_url || '');
    setEditWatchPermission(watch.connector_mode === 'generic_web_llm' || watch.connector === 'generic_web_llm');
    setError('');
  };

  const cancelWatchEdit = () => { setEditingWatchId(''); setEditWatchUrl(''); setEditWatchPermission(false); };

  const saveWatchEdit = async (event, watch) => {
    event.preventDefault();
    if (!editWatchUrl.trim()) return setError('Enter a listings URL.');
    setSaving(true); setError(''); setNotice('');
    try {
      const generic = editWatchPermission;
      const data = await requestJson(`${baseUrl}/portfolios/${encodeURIComponent(activeId)}/watches/${encodeURIComponent(watch.watch_source_id)}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': newKey('watch-replace') },
        body: JSON.stringify({
          source_url: editWatchUrl,
          connector: generic ? 'generic_web_llm' : 'colliers_tampa',
          connector_mode: generic ? 'generic_web_llm' : 'deterministic',
          permission_attested: generic,
          expected_updated_at: watch.updated_at,
        }),
      });
      cancelWatchEdit();
      await loadWatches();
      if (data.source_version?.watch_source_version_id) {
        await syncWatch(watch, data.source_version.watch_source_version_id);
      }
      setNotice(data.created ? 'URL watch change staged for validation.' : 'This URL watch change is already staged.');
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const syncWatch = async (watch, sourceVersionId = null) => {
    setSaving(true); setError(''); setNotice('');
    try {
      await requestJson(`${baseUrl}/portfolios/${encodeURIComponent(activeId)}/watches/${encodeURIComponent(watch.watch_source_id)}/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Idempotency-Key': newKey('watch-sync') },
        body: JSON.stringify(sourceVersionId ? { source_version_id: sourceVersionId } : {}),
      });
      await loadWatches();
      setNotice('Watch synchronization queued.');
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const controlWatch = async (watch, action) => {
    setSaving(true); setError(''); setNotice('');
    try {
      await requestJson(`${baseUrl}/portfolios/${encodeURIComponent(activeId)}/watches/${encodeURIComponent(watch.watch_source_id)}/${action}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ expected_updated_at: watch.updated_at }),
      });
      await loadWatches();
      setNotice(`Watch ${action === 'pause' ? 'paused' : 'resumed'}.`);
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const removeWatch = async watch => {
    if (!window.confirm('Remove this URL watch? Watch-only listings will be archived, while manual and CSV rows remain.')) return;
    setSaving(true); setError(''); setNotice('');
    try {
      await requestJson(`${baseUrl}/portfolios/${encodeURIComponent(activeId)}/watches/${encodeURIComponent(watch.watch_source_id)}`, {
        method: 'DELETE', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ expected_updated_at: watch.updated_at }),
      });
      await Promise.all([loadWatches(), loadRows()]);
      setNotice('URL watch removed; retained parcel history was not deleted.');
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const openManualPull = (sourceUrl = '') => {
    setManualPullUrl(sourceUrl || '');
    setManualPullPermission(false);
    setManualPullMessage('');
    setManualPullComplete(false);
    setManualPullSelected(new Set());
    setManualPullReview(null);
    setManualPullStreamFrame('');
    setManualPullStreamState('connecting');
    setManualPullOpen(true);
  };

  const closeManualPullStream = () => {
    if (manualPullStreamRef.current) {
      manualPullStreamRef.current.onclose = null;
      manualPullStreamRef.current.close();
      manualPullStreamRef.current = null;
    }
    manualPullStreamTicketRef.current = '';
    setManualPullStreamState('closed');
  };

  const connectManualPullStream = async (session, sessionToken) => {
    if (!session || !sessionToken || !organizationId || !activeId || typeof WebSocket === 'undefined') {
      setManualPullStreamState('fallback');
      return false;
    }
    try {
      const ticketData = await requestJson(`${baseUrl}/portfolios/${encodeURIComponent(activeId)}/manual-pull/sessions/${encodeURIComponent(session.session_id)}/stream-ticket`, {
        method: 'POST', headers: { 'X-Portfolio-Session-Token': sessionToken },
      });
      const streamOrigin = new URL(ticketData.origin || window.location.origin);
      const protocol = streamOrigin.protocol === 'https:' ? 'wss:' : 'ws:';
      const streamPath = `${baseUrl}/portfolios/${encodeURIComponent(activeId)}/manual-pull/sessions/${encodeURIComponent(session.session_id)}/stream`;
      const socket = new WebSocket(`${protocol}//${streamOrigin.host}${streamPath}`);
      manualPullStreamRef.current = socket;
      manualPullStreamTicketRef.current = ticketData.stream_ticket || '';
      setManualPullStreamState('connecting');
      socket.onopen = () => socket.send(JSON.stringify({ type: 'attach', session_id: session.session_id, ticket: ticketData.stream_ticket }));
      socket.onmessage = event => {
        let message;
        try { message = JSON.parse(event.data); } catch (_error) { return; }
        if (message.type === 'attached') {
          setManualPullStreamState('connected');
          if (message.session) setManualPullSession(message.session);
        } else if (message.type === 'status' && message.session) {
          setManualPullSession(message.session);
        } else if (message.type === 'frame' && message.data) {
          setManualPullStreamFrame(`data:image/jpeg;base64,${message.data}`);
        } else if (message.type === 'input_ack' && message.result?.ok === false) {
          setManualPullMessage(message.result.error || 'Browser input was rejected.');
        } else if (message.type === 'error') {
          setManualPullMessage(message.error || 'Live browser stream error.');
        }
      };
      socket.onerror = () => {
        setManualPullStreamState('fallback');
        setManualPullMessage('Live browser stream unavailable; using the safe fallback transport.');
      };
      socket.onclose = () => {
        if (manualPullStreamRef.current === socket) {
          manualPullStreamRef.current = null;
          setManualPullStreamState(previous => previous === 'closed' ? previous : 'fallback');
        }
      };
      return true;
    } catch (err) {
      setManualPullStreamState('fallback');
      setManualPullMessage(err.message || 'Live browser stream could not be opened.');
      return false;
    }
  };

  const startManualPull = async event => {
    event.preventDefault();
    if (!activeId) return setError('Create or select a portfolio first.');
    if (!manualPullUrl.trim()) return setManualPullMessage('Enter a listings URL.');
    if (!manualPullPermission) return setManualPullMessage('Confirm permission before opening the source.');
    setSaving(true); setError(''); setManualPullMessage('Opening a temporary browser session…');
    try {
      const data = await requestJson(`${baseUrl}/portfolios/${encodeURIComponent(activeId)}/manual-pull/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Idempotency-Key': newKey('manual-pull-session') },
        body: JSON.stringify({ source_url: manualPullUrl, permission_attested: true }),
      });
      setManualPullSession(data.session);
      setManualPullToken(data.session_token || '');
      setManualPullMessage('Browser opened. Complete any legitimate site challenge, then pull all listings.');
      await connectManualPullStream(data.session, data.session_token || '');
    } catch (err) { setManualPullMessage(err.message); } finally { setSaving(false); }
  };

  const beginRowEdit = row => {
    const input = row?.normalized_input || {};
    setEditingRow(row);
    setEditRowIdentity({
      address: input.address || '', apn: input.apn || '',
      latitude: input.latitude ?? '', longitude: input.longitude ?? '',
    });
    setError('');
  };

  const saveRowEdit = async event => {
    event.preventDefault();
    if (!editingRow || !activeId) return;
    setSaving(true); setError(''); setNotice('Validating the corrected listing identity…');
    try {
      const data = await requestJson(`${baseUrl}/portfolios/${encodeURIComponent(activeId)}/rows/${encodeURIComponent(editingRow.row_id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Idempotency-Key': newKey('row-identity-edit') },
        body: JSON.stringify({ ...editRowIdentity, identity_revision: editingRow.identity_revision ?? 0 }),
      });
      setEditingRow(null);
      setNotice(data.validation?.state === 'validated'
        ? 'Listing identity validated. Availability checks can now run.'
        : `Listing saved for review: ${data.validation?.reason || 'identity validation did not complete'}.`);
      await loadRows();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const closeManualPull = async () => {
    const session = manualPullSession;
    closeManualPullStream();
    try {
      if (session && manualPullToken && activeId) {
        await requestJson(`${baseUrl}/portfolios/${encodeURIComponent(activeId)}/manual-pull/sessions/${encodeURIComponent(session.session_id)}`, {
          method: 'DELETE', headers: { 'X-Portfolio-Session-Token': manualPullToken },
        });
      }
    } catch (_error) { /* expiry/cleanup is safe; close the modal locally */ }
    if (manualPullViewRef.current) {
      URL.revokeObjectURL(manualPullViewRef.current);
      manualPullViewRef.current = '';
    }
    setManualPullOpen(false);
    setManualPullSession(null);
    setManualPullToken('');
    setManualPullViewUrl('');
    setManualPullStreamFrame('');
    setManualPullMessage('');
    setManualPullReview(null);
    setManualPullComplete(false);
    setManualPullSelected(new Set());
  };

  const sendManualPullInput = event => {
    if (!manualPullBase || !manualPullToken) return Promise.resolve();
    const socket = manualPullStreamRef.current;
    if (manualPullStreamState === 'connected' && typeof WebSocket !== 'undefined' && socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: 'input', event }));
      return Promise.resolve();
    }
    const run = async () => {
      try {
        const data = await requestJson(`${manualPullBase}/input`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Portfolio-Session-Token': manualPullToken },
          body: JSON.stringify(event),
        });
        setManualPullSession(data.session);
        // Refresh only after a completed click/release.  Keeping the image
        // stable during down/up preserves the target under the pointer.
        if (event.type === 'up' || event.type === 'click') await refreshManualPullView(data.session, manualPullToken);
      } catch (err) {
        if (/manual_pull_session_(?:not_found|expired|not_active)/i.test(err.message || '')) {
          if (manualPullViewRef.current) URL.revokeObjectURL(manualPullViewRef.current);
          manualPullViewRef.current = '';
          setManualPullViewUrl('');
          setManualPullSession(null);
          setManualPullToken('');
          setManualPullMessage('This browser session is no longer active. Reopen the source to continue.');
        } else setManualPullMessage(err.message);
      }
    };
    const queued = manualPullInputQueueRef.current.then(run, run);
    manualPullInputQueueRef.current = queued.catch(() => {});
    return queued;
  };

  const manualPullPoint = event => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const image = event.currentTarget;
    const scaleX = (image.naturalWidth || 1440) / Math.max(bounds.width, 1);
    const scaleY = (image.naturalHeight || 1000) / Math.max(bounds.height, 1);
    return { x: (event.clientX - bounds.left) * scaleX, y: (event.clientY - bounds.top) * scaleY };
  };

  const pointerMoveManualPullView = event => {
    const bounds = event.currentTarget.getBoundingClientRect();
    setManualPullCursor({ left: `${Math.max(0, Math.min(100, ((event.clientX - bounds.left) / Math.max(bounds.width, 1)) * 100))}%`, top: `${Math.max(0, Math.min(100, ((event.clientY - bounds.top) / Math.max(bounds.height, 1)) * 100))}%` });
    // Hover events are useful for menus, but must be throttled so they cannot
    // fill the bounded worker input queue while the pointer is moving.
    const now = Date.now();
    if (now - manualPullStreamMoveRef.current >= 60) {
      manualPullStreamMoveRef.current = now;
      sendManualPullInput({ type: 'move', ...manualPullPoint(event) });
    }
  };

  const pointerButtonManualPullView = (event, phase) => {
    event.preventDefault();
    const buttons = ['left', 'middle', 'right'];
    sendManualPullInput({ type: phase, button: buttons[event.button] || 'left', ...manualPullPoint(event) });
  };

  const wheelManualPullView = event => {
    // React/browser wheel listeners may be passive, so preventDefault() is
    // ignored (and logs a console warning). Stop propagation instead while
    // forwarding the native delta to the remote browser session.
    event.stopPropagation();
    const dy = Math.max(-2000, Math.min(2000, Number(event.deltaY) || 0));
    const dx = Math.max(-2000, Math.min(2000, Number(event.deltaX) || 0));
    if (dx || dy) sendManualPullInput({ type: 'scroll', dx, dy });
  };

  const keyDownManualPullView = event => {
    // Let users interact with the remote page directly after focusing the
    // browser surface, while keeping the modal's own form controls usable.
    if (event.key && event.key !== 'Unidentified') {
      event.preventDefault();
      const modifiers = [];
      if (event.ctrlKey) modifiers.push('Control');
      if (event.altKey) modifiers.push('Alt');
      if (event.shiftKey && event.key !== 'Shift') modifiers.push('Shift');
      if (event.metaKey) modifiers.push('Meta');
      sendManualPullInput({ type: 'key', key: [...modifiers, event.key].join('+') });
    }
  };


  const captureManualPullPage = async (complete = false) => {
    if (!manualPullBase || !manualPullToken) return null;
    setSaving(true);
    setManualPullMessage('Capturing and sanitizing the current page…');
    try {
      const data = await requestJson(`${manualPullBase}/capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Portfolio-Session-Token': manualPullToken },
        body: JSON.stringify({ complete }),
      });
      setManualPullSession(data.session);
      setManualPullMessage(data.created === false
        ? 'This page was already captured; no duplicate artifact was created.'
        : `Captured page ${data.capture?.sequence || ''}. It is ready for the next extraction phase.`);
      await refreshManualPullView(data.session, manualPullToken);
      return data;
    } catch (err) { setManualPullMessage(err.message); throw err; } finally { setSaving(false); }
  };

  const requestManualPullData = () => pullAllManualPullData();

  const nextManualPullPage = async () => {
    if (!manualPullBase || !manualPullToken) return null;
    setSaving(true);
    setManualPullMessage('Opening the next same-source page…');
    try {
      const data = await requestJson(`${manualPullBase}/next-page`, {
        method: 'POST', headers: { 'X-Portfolio-Session-Token': manualPullToken },
      });
      setManualPullSession(data.session);
      setManualPullMessage(`Page ${data.session?.page_number || ''} is ready to capture.`);
      await refreshManualPullView(data.session, manualPullToken);
      return data;
    } catch (err) { setManualPullMessage(err.message); throw err; } finally { setSaving(false); }
  };

  const reviewManualPull = async (sessionOverride = manualPullSession) => {
    if (!manualPullBase || !manualPullToken || !sessionOverride?.capture_count) return null;
    setSaving(true);
    setManualPullMessage('Extracting captured listings and validating identities…');
    try {
      const data = await requestJson(`${manualPullBase}/review`, {
        method: 'POST', headers: { 'X-Portfolio-Session-Token': manualPullToken },
      });
      setManualPullSession(data.session);
      setManualPullReview(data.review || null);
      const defaults = (data.review?.candidates || []).filter(item => item.state === 'validated').map(item => item.candidate_id);
      setManualPullSelected(new Set(defaults));
      setManualPullMessage(`Review ready: ${data.review?.candidate_count || 0} listing candidates. Portfolio rows were not changed.`);
      return data;
    } catch (err) { setManualPullMessage(err.message); throw err; } finally { setSaving(false); }
  };

  const pullAllManualPullData = async () => {
    if (!manualPullBase || !manualPullToken) return;
    setSaving(true);
    try {
      const data = await requestJson(`${manualPullBase}/jobs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Portfolio-Session-Token': manualPullToken, 'Idempotency-Key': newKey('manual-pull-job') },
        body: JSON.stringify({}),
      });
      setManualPullJob(data.job || null);
      setManualPullMessage('Listing pull started in the background. This window will close while listings are analyzed.');
      closeManualPullStream();
      if (manualPullViewRef.current) { URL.revokeObjectURL(manualPullViewRef.current); manualPullViewRef.current = ''; }
      setManualPullOpen(false);
    } catch (err) {
      setManualPullMessage(err.message || 'Listing pull failed.');
    } finally { setSaving(false); }
  };

  const toggleManualPullCandidate = candidateId => {
    setManualPullSelected(previous => {
      const next = new Set(previous);
      if (next.has(candidateId)) next.delete(candidateId); else next.add(candidateId);
      return next;
    });
  };

  const importManualPullListings = async () => {
    if (!manualPullBase || !manualPullToken || !manualPullReview || !manualPullSelected.size) return;
    setSaving(true);
    setManualPullMessage('Importing approved listings into this portfolio…');
    try {
      const validatedCount = Number(manualPullReview.validated_count || 0);
      const completeInventoryConfirmed = Boolean(
        manualPullComplete
        && manualPullReview.archival_eligible
        && validatedCount > 0
        && manualPullSelected.size === validatedCount
      );
      const data = await requestJson(`${manualPullBase}/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Portfolio-Session-Token': manualPullToken, 'Idempotency-Key': newKey('manual-pull-import') },
        body: JSON.stringify({
          review_id: manualPullReview.review_id,
          candidate_ids: Array.from(manualPullSelected),
          complete_inventory_confirmed: completeInventoryConfirmed,
        }),
      });
      await Promise.all([loadRows(), loadWatches()]);
      const message = `Imported ${data.counts?.added || 0} new listing(s); ${data.counts?.updated || 0} updated, ${data.counts?.unresolved || 0} unresolved. Existing manual and CSV rows were preserved.${data.archival_enabled ? ' Confirmed absent URL-watch listings were eligible for archival.' : ' Inventory remains partial; no URL-watch rows were archived.'}`;
      await closeManualPull();
      setNotice(message);
    } catch (err) { setManualPullMessage(err.message); } finally { setSaving(false); }
  };

  const toggleArchived = async watch => {
    if (showArchived) { setShowArchived(false); return; }
    setSaving(true); setError('');
    try {
      const data = await requestJson(`${baseUrl}/portfolios/${encodeURIComponent(activeId)}/watches/${encodeURIComponent(watch.watch_source_id)}/archived-listings`);
      setArchivedListings(data.listings || []);
      setShowArchived(true);
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const deleteRow = async row => {
    const identity = row.normalized_input?.address || row.normalized_input?.apn || row.row_id;
    if (!window.confirm(`Remove ${identity} from this portfolio?`)) return;
    setSaving(true); setError(''); setNotice('');
    try {
      const deleted = { portfolioId: activeId, rowId: row.row_id, identity };
      await requestJson(`${baseUrl}/portfolios/${encodeURIComponent(activeId)}/rows/${encodeURIComponent(row.row_id)}`, { method: 'DELETE' });
      await loadRows();
      setDeletedRow(deleted);
      setNotice(`${identity} removed from the portfolio.`);
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const restoreRow = async () => {
    if (!deletedRow) return;
    setSaving(true); setError('');
    try {
      await requestJson(`${baseUrl}/portfolios/${encodeURIComponent(deletedRow.portfolioId)}/rows/${encodeURIComponent(deletedRow.rowId)}/restore`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': newKey('restore-row') }, body: '{}',
      });
      if (activeId === deletedRow.portfolioId) await loadRows();
      else await loadPortfolios(deletedRow.portfolioId, false);
      setNotice(`${deletedRow.identity} restored to the portfolio.`);
      setDeletedRow(null);
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const submitReports = async (selectionScope = 'selected') => {
    const rowIds = [...selected].filter(rowId => rows.some(row => row.row_id === rowId && rowEligible(row)));
    if (!rowIds.length) return setError('Select at least one parcel with available parcel data.');
    const signature = `${activeId}:${selectionScope}:${[...rowIds].sort().join(',')}`;
    if (!reportIdentityRef.current || reportIdentityRef.current.signature !== signature) {
      reportIdentityRef.current = { signature, key: newKey('portfolio-reports') };
    }
    setSaving(true); setError(''); setNotice('');
    try {
      const data = await requestJson(`${baseUrl}/portfolios/${encodeURIComponent(activeId)}/report-jobs`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': reportIdentityRef.current.key },
        body: JSON.stringify({ row_ids: rowIds, selection_scope: selectionScope }),
      });
      reportIdentityRef.current = null;
      setSelected(new Set());
      await refreshReportJobs();
      setNotice(`${data.accepted_count} report${data.accepted_count === 1 ? '' : 's'} queued${data.rejected_count ? `; ${data.rejected_count} not eligible` : ''}.`);
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const controlReport = async (job, action) => {
    setSaving(true); setError(''); setNotice('');
    try {
      await requestJson(`${baseUrl}/report-jobs/${encodeURIComponent(job.association_id)}/${action}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      await refreshReportJobs();
      setNotice(action === 'retry' ? 'Report retry queued.' : 'Report cancelled.');
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  if (loading) return <div className="flex min-h-screen items-center justify-center" role="status">Loading Land Portfolio…</div>;
  if (!context?.portfolio_enabled) return <div className="mx-auto max-w-xl p-8"><h1 className="text-2xl font-bold">Land Portfolio unavailable</h1><p className="mt-3 text-gray-600">Your account does not have access to this feature.</p><a className="mt-5 inline-block text-blue-700" href="/analysis">Return to Acquisition Intel</a></div>;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <header className="border-b bg-white px-4 py-3 shadow-sm">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3"><img src={lbIcon} className="h-10 w-10 object-contain" alt="LandBuilder" /><div><h1 className="text-xl font-bold text-blue-700">Land Portfolio</h1><p className="text-xs text-slate-500">Saved parcel workspace</p></div></div>
          <nav className="flex items-center gap-2" aria-label="Portfolio navigation"><a href="/analysis" className="inline-flex items-center gap-2 rounded-lg px-3 py-2 hover:bg-slate-100"><ArrowLeft size={17} />Acquisition Intel</a><a href="/auth/logout" className="inline-flex items-center gap-2 rounded-lg px-3 py-2 hover:bg-red-50 hover:text-red-700"><LogOut size={17} />Logout</a></nav>
        </div>
      </header>
      <main className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6">
        {error && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-800">{error}</div>}
        {notice && <div role="status" aria-live="polite" className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-emerald-800"><span>{notice}</span>{deletedPortfolio && <button type="button" disabled={saving} onClick={restorePortfolio} className="rounded border border-emerald-700 px-3 py-1 font-medium disabled:opacity-50">Undo portfolio delete</button>}{deletedRow && <button type="button" disabled={saving} onClick={restoreRow} className="rounded border border-emerald-700 px-3 py-1 font-medium disabled:opacity-50">Undo parcel removal</button>}</div>}
        <section className="rounded-xl border bg-white p-4 shadow-sm" aria-labelledby="portfolio-heading">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[220px] flex-1"><label className="mb-1 block text-sm font-medium" htmlFor="portfolio-select" id="portfolio-heading">Portfolio</label><select id="portfolio-select" value={activeId} onChange={event => setActiveId(event.target.value)} className="w-full rounded-lg border px-3 py-2"><option value="">Select a portfolio</option>{portfolios.map(item => <option value={item.portfolio_id} key={item.portfolio_id}>{item.name}</option>)}</select></div>
            <button type="button" onClick={renamePortfolio} disabled={!activeId || saving} className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 disabled:opacity-50"><Edit3 size={16} />Rename</button>
            <button type="button" onClick={deletePortfolio} disabled={!activeId || saving || context.portfolio_role !== 'portfolio_admin'} className="inline-flex items-center gap-2 rounded-lg border border-red-200 px-3 py-2 text-red-700 disabled:opacity-50"><Trash2 size={16} />Delete</button>
          </div>
          <form onSubmit={createPortfolio} className="mt-4 flex flex-wrap gap-2"><label className="sr-only" htmlFor="new-portfolio">New portfolio name</label><input id="new-portfolio" value={portfolioName} onChange={event => setPortfolioName(event.target.value)} maxLength={120} placeholder="New portfolio name" className="min-w-[220px] flex-1 rounded-lg border px-3 py-2" /><button disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 font-medium text-white disabled:opacity-50"><Plus size={17} />Create portfolio</button></form>
        </section>

        <section className="rounded-xl border bg-white p-4 shadow-sm" aria-labelledby="manual-heading">
          <h2 id="manual-heading" className="font-semibold">Add parcel manually</h2><p className="mt-1 text-sm text-slate-500">Provide an address, APN, or both latitude and longitude.</p>
          <form onSubmit={addManualRow} className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {[['address','Address'],['apn','APN'],['latitude','Latitude'],['longitude','Longitude']].map(([field,label]) => <label key={field} className="text-sm"><span className="mb-1 block font-medium">{label}</span><input value={manual[field]} onChange={event => setManual({...manual, [field]: event.target.value})} inputMode={field === 'latitude' || field === 'longitude' ? 'decimal' : 'text'} className="w-full rounded-lg border px-3 py-2" /></label>)}
            <button disabled={!activeId || saving} className="self-end rounded-lg bg-emerald-600 px-4 py-2 font-medium text-white disabled:opacity-50">Add parcel</button>
          </form>
        </section>

        <section className="rounded-xl border bg-white p-4 shadow-sm" aria-labelledby="csv-heading">
          <div className="flex flex-wrap items-start justify-between gap-3"><div><h2 id="csv-heading" className="font-semibold">Import parcels from CSV</h2><p className="mt-1 text-sm text-slate-500">UTF-8 CSV, up to 10 MiB and 500 rows. Each row needs an address, APN, or latitude and longitude.</p></div><a href="/api/portfolio/csv-template" download className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm text-blue-700"><Download size={16} />Download template</a></div>
          <form onSubmit={importCsv} className="mt-3 flex flex-wrap items-end gap-3">
            <label className="min-w-[240px] flex-1 text-sm"><span className="mb-1 block font-medium">CSV file</span><input key={csvInputVersion} type="file" accept=".csv,text/csv" onChange={event => { const file = event.target.files?.[0] || null; setCsvFile(file); importIdentityRef.current = file ? { signature: `${activeId}:${file.name}:${file.size}:${file.lastModified}`, key: newKey('csv-import') } : null; }} className="block w-full rounded-lg border p-2" /></label>
            <button disabled={!activeId || !csvFile || saving} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 font-medium text-white disabled:opacity-50"><Upload size={17} />Import CSV</button>
          </form>
          {importResult && <div className="mt-4 rounded-lg bg-slate-50 p-3 text-sm" aria-label="CSV import results"><p className="font-medium">{importResult.accepted_count} accepted · {importResult.rejected_count} rejected · {importResult.duplicate_count || 0} duplicates</p>{importResult.ignored_headers?.length > 0 && <p className="mt-1 text-amber-800">Ignored columns: {importResult.ignored_headers.join(', ')}</p>}{importResult.row_results?.some(item => item.status === 'rejected') && <ul className="mt-2 list-disc pl-5 text-red-700">{importResult.row_results.filter(item => item.status === 'rejected').map(item => <li key={item.line}>Line {item.line}: {item.errors.join('; ')}</li>)}</ul>}</div>}
        </section>

        {urlWatchUiEnabled && (watchEnabled || watches.length > 0 || manualPullEnabled) && <section className="rounded-xl border bg-white p-4 shadow-sm" aria-labelledby="watch-heading">
          <h2 id="watch-heading" className="font-semibold">Watch listings from URL</h2>
          <p className="mt-1 text-sm text-slate-500">Add a Colliers pilot watch or authorize a bounded generalized URL watch. Watched rows are maintained separately from manual and CSV entries.</p>
          {manualPullEnabled && <div className="mt-4 rounded-lg border border-indigo-200 bg-indigo-50 p-3" aria-labelledby="manual-pull-heading">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div><h3 id="manual-pull-heading" className="font-semibold text-indigo-950">Manual URL pull (pilot)</h3><p className="mt-1 text-sm text-indigo-900">Open the source in a temporary browser, complete any legitimate human gate, pull the visible listings, and review them before import. Nothing runs automatically.</p></div>
              <button type="button" onClick={() => openManualPull()} disabled={!activeId || saving} className="rounded-lg bg-indigo-700 px-3 py-2 text-sm font-medium text-white disabled:opacity-50">Add URL / Open source</button>
            </div>
            <button type="button" disabled className="mt-3 rounded border border-indigo-300 bg-white px-3 py-2 text-sm text-indigo-900 opacity-80" title="Automatic watching is disabled for this pilot">Auto watch — Contact Acquisition Intel to enable</button>
          </div>}
          {!watchEnabled && !manualPullEnabled && <p className="mt-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">URL-watch management is temporarily disabled. Existing watches and their provenance remain visible; an administrator can resume management when the pilot is enabled.</p>}
          {watchEnabled && context.portfolio_role === 'portfolio_admin' && <form onSubmit={createWatch} className="mt-3 flex flex-wrap items-end gap-3">
            <label className="min-w-[280px] flex-1 text-sm"><span className="mb-1 block font-medium">Listings URL</span><input type="url" value={watchUrl} onChange={event => setWatchUrl(event.target.value)} placeholder="https://example.org/listings" className="w-full rounded-lg border px-3 py-2" /></label>
            <label className="flex max-w-sm items-center gap-2 pb-2 text-sm"><input type="checkbox" aria-label="I have permission to scrape this URL" checked={watchPermission} onChange={event => setWatchPermission(event.target.checked)} /><span>I have permission to scrape this URL</span></label>
            <button disabled={!activeId || !watchUrl.trim() || saving} className="rounded-lg bg-indigo-600 px-4 py-2 font-medium text-white disabled:opacity-50">Add URL watch</button>
          </form>}
          {context.portfolio_role !== 'portfolio_admin' && <p className="mt-3 rounded-lg bg-slate-50 p-3 text-sm text-slate-600">Portfolio administrators manage recurring URL watches. You can view their status and use active parcel rows.</p>}
          <div className="mt-4 space-y-3" aria-label="URL watches">
            {watches.length === 0 ? <p className="text-sm text-slate-500">No URL watches configured.</p> : watches.map(watch => <article key={watch.watch_source_id} className="rounded-lg border p-3">
              <div className="flex flex-wrap items-start justify-between gap-3"><div className="min-w-0"><div className="flex flex-wrap items-center gap-2"><span className={`rounded-full px-2 py-1 text-xs ${watch.status === 'active' ? 'bg-emerald-50 text-emerald-800' : watch.status === 'paused' ? 'bg-amber-50 text-amber-800' : 'bg-red-50 text-red-800'}`}>{watch.status}</span><span className="text-sm font-medium">{watchLabel(watch)}</span></div><a href={watch.normalized_url} target="_blank" rel="noreferrer" className="mt-2 block max-w-3xl truncate text-sm text-blue-700 underline">{watch.normalized_url}</a><dl className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-600"><div><dt className="inline font-medium">Source host: </dt><dd className="inline">{watch.hostname || '—'}</dd></div><div><dt className="inline font-medium">Last successful sync: </dt><dd className="inline">{watch.last_success_at ? new Date(watch.last_success_at).toLocaleString() : 'Not yet run'}</dd></div><div><dt className="inline font-medium">Next run: </dt><dd className="inline">{watch.next_run_at ? new Date(watch.next_run_at).toLocaleString() : 'Paused'}</dd></div><div><dt className="inline font-medium">Active: </dt><dd className="inline">{watch.active_listing_count || 0}</dd></div><div><dt className="inline font-medium">Archived: </dt><dd className="inline">{watch.archived_listing_count || 0}</dd></div><div><dt className="inline font-medium">Review/unresolved: </dt><dd className="inline">{watch.review_listing_count || 0}/{watch.unresolved_listing_count || 0}</dd></div>{watch.last_run && <div><dt className="inline font-medium">Last run: </dt><dd className="inline">{watch.last_run.status} · {watch.last_run.counts?.added || 0} added · {watch.last_run.counts?.archived || 0} archived</dd></div>}{watch.usage_summary && <div><dt className="inline font-medium">Usage: </dt><dd className="inline">{watch.usage_summary.llm_extraction_calls || 0} extraction calls · {watch.usage_summary.browser_requests || 0} browser requests</dd></div>}</dl>{watch.safe_error && <p className="mt-2 text-xs text-red-700">{watch.safe_error}</p>}</div>
              {watchEnabled && context.portfolio_role === 'portfolio_admin' && <div className="flex flex-wrap gap-2"><button type="button" onClick={() => syncWatch(watch)} disabled={saving || watch.status !== 'active'} className="rounded border px-3 py-2 text-sm disabled:opacity-50">Sync now</button><button type="button" onClick={() => beginWatchEdit(watch)} disabled={saving} className="rounded border px-3 py-2 text-sm disabled:opacity-50">Edit URL</button><button type="button" onClick={() => controlWatch(watch, watch.status === 'paused' ? 'resume' : 'pause')} disabled={saving} className="rounded border px-3 py-2 text-sm disabled:opacity-50">{watch.status === 'paused' ? 'Resume' : 'Pause'}</button><button type="button" onClick={() => removeWatch(watch)} disabled={saving} className="rounded border border-red-200 px-3 py-2 text-sm text-red-700 disabled:opacity-50">Remove</button></div>}
              {manualPullEnabled && context.portfolio_role === 'portfolio_admin' && <button type="button" onClick={() => openManualPull(watch.normalized_url || watch.source_url)} disabled={saving} className="mt-2 rounded border border-indigo-300 px-3 py-2 text-sm text-indigo-800 disabled:opacity-50">Refresh listings</button>}
              </div>
              {editingWatchId === watch.watch_source_id && <form onSubmit={event => saveWatchEdit(event, watch)} className="mt-3 flex flex-wrap items-end gap-3 rounded bg-slate-50 p-3"><label className="min-w-[280px] flex-1 text-sm"><span className="mb-1 block font-medium">Replacement URL</span><input type="url" value={editWatchUrl} onChange={event => setEditWatchUrl(event.target.value)} className="w-full rounded-lg border px-3 py-2" /></label><label className="flex items-center gap-2 pb-2 text-sm"><input type="checkbox" aria-label="I have permission to scrape the replacement URL" checked={editWatchPermission} onChange={event => setEditWatchPermission(event.target.checked)} /><span>Permission confirmed</span></label><button type="submit" disabled={saving || !editWatchUrl.trim()} className="rounded bg-indigo-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50">Save URL</button><button type="button" onClick={cancelWatchEdit} disabled={saving} className="rounded border px-3 py-2 text-sm">Cancel</button></form>}
              <button type="button" onClick={() => toggleArchived(watch)} disabled={saving} className="mt-3 text-sm text-blue-700 underline">{showArchived ? 'Hide archived listings' : `View archived listings (${watch.archived_listing_count || 0})`}</button>
              {showArchived && <div className="mt-3 rounded bg-slate-50 p-3" aria-label="Archived URL-watch listings">{archivedListings.length === 0 ? <p className="text-sm text-slate-500">No archived listings.</p> : <ul className="space-y-2 text-sm">{archivedListings.map(listing => <li key={listing.watch_listing_id}><a href={listing.canonical_url} target="_blank" rel="noreferrer" className="text-blue-700 underline">{listing.normalized_candidate?.title || listing.external_listing_id}</a><span className="ml-2 text-xs text-slate-500">Archived {listing.inactive_at ? new Date(listing.inactive_at).toLocaleString() : ''}</span></li>)}</ul>}</div>}
            </article>)}
          </div>
        </section>}

        <section className="rounded-xl border bg-white shadow-sm" aria-labelledby="rows-heading">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b p-4"><div><h2 id="rows-heading" className="font-semibold">Portfolio parcels</h2><p className="text-sm text-slate-500">{visibleRows.length} rows · {selected.size} selected</p></div><div className="flex flex-wrap items-center gap-2"><button type="button" onClick={selectAllFiltered} disabled={!visibleRows.some(rowEligible) || saving} className="rounded-lg border px-3 py-2 text-sm disabled:opacity-50">Select all filtered</button><button type="button" onClick={() => submitReports(selectedScope())} disabled={!selected.size || saving} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"><FileText size={16} />Generate reports</button><label className="text-sm"><span className="sr-only">Filter by origin</span><select aria-label="Filter by origin" value={originFilter} onChange={event => { setOriginFilter(event.target.value); setPage(1); setSelected(new Set()); }} className="rounded-lg border px-3 py-2"><option value="all">All origins</option><option value="manual">Manual</option><option value="csv_import">CSV import</option><option value="url_watch">URL watch</option></select></label><label className="relative"><span className="sr-only">Filter parcels</span><Search className="absolute left-3 top-2.5 text-slate-400" size={16} /><input value={filter} onChange={event => { setFilter(event.target.value); setPage(1); setSelected(new Set()); }} placeholder="Filter address or APN" className="rounded-lg border py-2 pl-9 pr-3" /></label></div></div>
          {!activeId ? <div className="p-8 text-center text-slate-500">Create or select a portfolio to begin.</div> : rows.length === 0 ? <div className="p-8 text-center"><Rows3 className="mx-auto text-slate-300" size={36} /><p className="mt-2 font-medium">No parcels yet</p><p className="text-sm text-slate-500">Add your first parcel using the form above.</p></div> : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y" aria-label="Portfolio parcels">
                <thead className="bg-slate-50"><tr>
                  <th className="p-3 text-left"><input type="checkbox" aria-label="Select all rows on this page" checked={pagedRows.filter(rowEligible).length > 0 && pagedRows.filter(rowEligible).every(row => selected.has(row.row_id))} onChange={togglePage} /></th>
                  <th className="p-3 text-left"><button onClick={() => toggleSort('identity')}>Parcel identity</button></th>
                  <th className="p-3 text-left">Coordinates</th><th className="p-3 text-left">Data availability</th><th className="p-3 text-left">Report status</th><th className="p-3 text-right">Actions</th>
                </tr></thead>
                <tbody className="divide-y">{pagedRows.map(row => {
                  const job = reportJobs[row.row_id];
                  const resolved = availability[row.row_id]?.parcel?.result || {};
                  const watchOrigin = (row.origins || []).find(item => item.origin === 'url_watch');
                  // A listing coordinate can resolve to a parcel whose situs address
                  // differs from the broker's advertised address. Preserve the source
                  // identity for watched listings while using Regrid's APN/geometry.
                  const address = watchOrigin
                    ? (row.normalized_input?.address || resolved.address)
                    : (resolved.address || row.normalized_input?.address);
                  const apn = resolved.apn || row.normalized_input?.apn;
                  const latitude = watchOrigin
                    ? (row.normalized_input?.latitude ?? resolved.latitude)
                    : (resolved.latitude ?? row.normalized_input?.latitude);
                  const longitude = watchOrigin
                    ? (row.normalized_input?.longitude ?? resolved.longitude)
                    : (resolved.longitude ?? row.normalized_input?.longitude);
                  const identity = address || apn || row.row_id;
                  const originLabels = { manual: 'Manual', csv_import: 'CSV import', url_watch: 'URL watch', legacy: 'Existing' };
                  return <tr key={row.row_id} className="hover:bg-slate-50">
                    <td className="p-3"><input type="checkbox" disabled={!rowEligible(row)} title={rowEligible(row) ? '' : 'Parcel data must be available before report submission'} aria-label={`Select ${identity}`} checked={selected.has(row.row_id)} onChange={() => toggleRow(row.row_id)} /></td>
                    <td className="p-3"><div className="font-medium">{address || 'Address unavailable'}</div><div className="text-xs text-slate-500">{apn || 'APN unavailable'}</div><div className="mt-1 flex flex-wrap gap-1">{(row.origins || []).map(origin => <span key={`${origin.origin}:${origin.origin_key}`} className="rounded bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700">{originLabels[origin.origin] || origin.origin}</span>)}{row.identity_validation_state && row.identity_validation_state !== 'validated' && <span className="rounded bg-amber-100 px-2 py-0.5 text-[11px] text-amber-900">{row.identity_validation_state.replace('_', ' ')}</span>}</div>{row.identity_validation_reason && row.identity_validation_state !== 'validated' && <div className="mt-1 max-w-xs text-xs text-amber-800">{row.identity_validation_reason}</div>}{watchOrigin?.canonical_url && <a href={watchOrigin.canonical_url} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs text-blue-700 underline">View source listing</a>}</td>
                    <td className="p-3 text-sm">{latitude != null && longitude != null ? `${latitude}, ${longitude}` : '—'}</td>
                    <td className="p-3"><div className="flex min-w-[260px] flex-wrap gap-1" aria-label={`Availability for ${identity}`}>{['parcel','wetlands','flood','elevation','soil','ownership','wildlife'].map(type => { const check = availability[row.row_id]?.[type]; const state = check?.state || 'pending'; return <span key={type} title={check?.safe_reason || ''} className={`rounded-full px-2 py-1 text-xs ${state === 'available' ? 'bg-emerald-50 text-emerald-800' : state === 'unavailable' ? 'bg-slate-100 text-slate-700' : state === 'failed' || state === 'dead_letter' ? 'bg-red-50 text-red-800' : 'bg-amber-50 text-amber-800'}`}>{type}: {state}</span>; })}</div></td>
                    <td className="p-3 text-sm"><span className={`rounded-full px-2 py-1 ${job?.status === 'delivered' || job?.status === 'ready' ? 'bg-emerald-50 text-emerald-800' : job?.status === 'failed' || job?.status === 'dead_letter' ? 'bg-red-50 text-red-800' : 'bg-slate-100 text-slate-700'}`}>{job?.status || 'not submitted'}</span>{job?.safe_reason && <div className="mt-1 max-w-[180px] text-xs text-slate-500">{job.safe_reason}</div>}</td>
                    <td className="p-3 text-right"><div className="flex justify-end gap-1">
                      <button type="button" onClick={() => beginRowEdit(row)} disabled={saving} aria-label={`Edit ${identity}`} className="rounded p-2 text-indigo-700 hover:bg-indigo-50"><Edit3 size={16} /></button>
                      {job?.retry_allowed && <button type="button" onClick={() => controlReport(job, 'retry')} disabled={saving} aria-label={`Retry report for ${identity}`} className="rounded p-2 text-blue-700 hover:bg-blue-50"><RefreshCw size={16} /></button>}
                      {job?.cancel_allowed && <button type="button" onClick={() => controlReport(job, 'cancel')} disabled={saving} aria-label={`Cancel report for ${identity}`} className="rounded p-2 text-amber-700 hover:bg-amber-50"><XCircle size={16} /></button>}
                      {job?.has_download && <a href={`${baseUrl}/report-jobs/${encodeURIComponent(job.association_id)}/download`} aria-label={`Download report for ${identity}`} className="rounded p-2 text-blue-700 hover:bg-blue-50"><Download size={16} /></a>}
                      <button type="button" onClick={() => deleteRow(row)} disabled={saving} aria-label={`Remove ${row.normalized_input?.address || row.normalized_input?.apn || 'parcel'}`} className="rounded p-2 text-red-700 hover:bg-red-50"><Trash2 size={16} /></button>
                    </div></td>
                  </tr>;
                })}</tbody>
              </table>
            </div>
          )}
          <div className="flex items-center justify-between border-t p-3 text-sm"><span>Page {page} of {pageCount}</span><div className="flex gap-2"><button aria-label="Previous portfolio page" disabled={page <= 1} onClick={() => setPage(value => Math.max(1, value - 1))} className="rounded border p-2 disabled:opacity-40"><ChevronLeft size={16} /></button><button aria-label="Next portfolio page" disabled={page >= pageCount} onClick={() => setPage(value => Math.min(pageCount, value + 1))} className="rounded border p-2 disabled:opacity-40"><ChevronRight size={16} /></button></div></div>
        </section>
        {editingRow && <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4" role="presentation">
          <section role="dialog" aria-modal="true" aria-labelledby="edit-row-dialog-title" className="w-full max-w-xl rounded-xl bg-white p-5 shadow-2xl">
            <h2 id="edit-row-dialog-title" className="text-lg font-semibold">Edit listing identifiers</h2>
            <p className="mt-1 text-sm text-slate-600">Correct the address, APN, or coordinates. The server will revalidate the identity before enabling data checks and reports.</p>
            <form onSubmit={saveRowEdit} className="mt-4 grid gap-3">
              <label className="text-sm"><span className="mb-1 block font-medium">Address</span><input value={editRowIdentity.address} onChange={event => setEditRowIdentity(value => ({ ...value, address: event.target.value }))} className="w-full rounded-lg border px-3 py-2" /></label>
              <label className="text-sm"><span className="mb-1 block font-medium">APN</span><input value={editRowIdentity.apn} onChange={event => setEditRowIdentity(value => ({ ...value, apn: event.target.value }))} className="w-full rounded-lg border px-3 py-2" /></label>
              <div className="grid gap-3 sm:grid-cols-2"><label className="text-sm"><span className="mb-1 block font-medium">Latitude</span><input inputMode="decimal" value={editRowIdentity.latitude} onChange={event => setEditRowIdentity(value => ({ ...value, latitude: event.target.value }))} className="w-full rounded-lg border px-3 py-2" /></label><label className="text-sm"><span className="mb-1 block font-medium">Longitude</span><input inputMode="decimal" value={editRowIdentity.longitude} onChange={event => setEditRowIdentity(value => ({ ...value, longitude: event.target.value }))} className="w-full rounded-lg border px-3 py-2" /></label></div>
              <div className="flex justify-end gap-2"><button type="button" onClick={() => setEditingRow(null)} disabled={saving} className="rounded-lg border px-4 py-2">Cancel</button><button type="submit" disabled={saving} className="rounded-lg bg-indigo-700 px-4 py-2 font-medium text-white disabled:opacity-50">{saving ? 'Validating…' : 'Save and validate'}</button></div>
            </form>
          </section>
        </div>}
        {manualPullOpen && <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4" role="presentation">
          <section role="dialog" aria-modal="true" aria-labelledby="manual-pull-dialog-title" className="flex max-h-[95vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
            <div className="flex flex-wrap items-start justify-between gap-3 border-b p-4"><div><h2 id="manual-pull-dialog-title" className="text-lg font-semibold">Manual URL pull</h2><p className="mt-1 text-sm text-slate-600">A temporary browser session is open for this source. Complete any legitimate human gate yourself.</p></div><div className="flex flex-wrap items-center gap-2">{manualPullReview && <button type="button" data-testid="manual-pull-import-listings" onClick={importManualPullListings} disabled={saving || !manualPullSelected.size} className="rounded-lg bg-emerald-700 px-3 py-2 text-sm font-medium text-white disabled:opacity-50">Import selected listings ({manualPullSelected.size})</button>}{manualPullSession && !manualPullReview && <button type="button" data-testid="manual-pull-all-listings" onClick={requestManualPullData} disabled={saving} className="rounded-lg bg-indigo-700 px-3 py-2 text-sm font-medium text-white disabled:opacity-50">Pull all listings</button>}<button type="button" onClick={closeManualPull} className="rounded border px-3 py-2 text-sm">Close session</button></div></div>
            {!manualPullSession ? <form onSubmit={startManualPull} className="space-y-4 overflow-y-auto p-5">
              <label className="block text-sm"><span className="mb-1 block font-medium">Listings URL</span><input autoFocus type="url" value={manualPullUrl} onChange={event => setManualPullUrl(event.target.value)} placeholder="https://example.org/listings" className="w-full rounded-lg border px-3 py-2" /></label>
              <label className="flex items-start gap-2 text-sm"><input type="checkbox" aria-label="I confirm that my organization has permission to retrieve and analyze listings from this website" checked={manualPullPermission} onChange={event => setManualPullPermission(event.target.checked)} className="mt-1" /><span>I confirm that my organization has permission to retrieve and analyze listings from this website.</span></label>
              <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">This confirmation does not bypass site terms, CAPTCHA, Cloudflare, authentication, rate limits, or network safety controls.</p>
              {manualPullMessage && <p role="status" className="text-sm text-indigo-800">{manualPullMessage}</p>}
              <div className="flex flex-wrap gap-2"><button type="submit" disabled={saving || !manualPullUrl.trim() || !manualPullPermission} className="rounded-lg bg-indigo-700 px-4 py-2 font-medium text-white disabled:opacity-50">Open source</button><button type="button" onClick={closeManualPull} className="rounded-lg border px-4 py-2">Cancel</button></div>
            </form> : <div className="grid min-h-0 gap-4 overflow-y-auto p-4 lg:grid-cols-[minmax(0,1fr)_280px]">
              <div className="min-h-[360px] rounded-lg bg-slate-950 p-2"><div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-200"><span>{manualPullSession.hostname} · {manualPullSession.state} · {manualPullStreamState === 'connected' ? 'live' : 'connecting'}</span><span>Expires {manualPullSession.expires_at ? new Date(manualPullSession.expires_at).toLocaleTimeString() : 'soon'}</span></div>{(manualPullStreamFrame || manualPullViewUrl) ? <div className="relative mx-auto w-fit max-w-full"><img src={manualPullStreamFrame || manualPullViewUrl} onPointerMove={pointerMoveManualPullView} onPointerDown={event => pointerButtonManualPullView(event, 'down')} onPointerUp={event => pointerButtonManualPullView(event, 'up')} onWheel={wheelManualPullView} onContextMenu={event => event.preventDefault()} onKeyDown={keyDownManualPullView} tabIndex={0} role="application" alt="Interactive remote listing browser view; move, click, drag, scroll, or right-click directly" className="mx-auto block max-h-[65vh] max-w-full cursor-default select-none object-contain outline-none focus:ring-2 focus:ring-indigo-400" draggable="false" />{manualPullCursor && <span aria-hidden="true" className="pointer-events-none absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-indigo-500 bg-white/60 shadow" style={manualPullCursor} />}</div> : <div className="flex min-h-[320px] items-center justify-center text-sm text-slate-300">Loading browser view…</div>}</div>
              <aside className="space-y-3">
                <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3">
                  <p className="text-sm font-medium text-indigo-950">Pull all listings</p>
                  <p className="mt-1 text-xs text-indigo-900">Use the live browser surface to finish any site gate and navigate to the listing results. The action is available as soon as the temporary session exists and follows same-source pagination automatically.</p>
                  {manualPullReview && (
                    <div className="mt-3 rounded bg-white p-2 text-xs text-indigo-950" aria-label="Manual pull results">
                      <dl className="grid grid-cols-2 gap-x-2 gap-y-1">
                        <div><dt className="inline font-medium">Pages:</dt> <dd className="inline">{manualPullReview.page_count || 0}</dd></div>
                        <div><dt className="inline font-medium">Listings:</dt> <dd className="inline">{manualPullReview.candidate_count || 0}</dd></div>
                        <div><dt className="inline font-medium">Validated:</dt> <dd className="inline">{manualPullReview.validated_count ?? (manualPullReview.candidates || []).filter(item => item.state === 'validated').length}</dd></div>
                        <div><dt className="inline font-medium">Needs review:</dt> <dd className="inline">{manualPullReview.review_count ?? (manualPullReview.candidates || []).filter(item => item.state === 'needs_review').length}</dd></div>
                        <div><dt className="inline font-medium">Unresolved:</dt> <dd className="inline">{manualPullReview.unresolved_count ?? (manualPullReview.candidates || []).filter(item => item.state === 'unresolved').length}</dd></div>
                        <div><dt className="inline font-medium">Duplicates:</dt> <dd className="inline">{manualPullReview.duplicate_count || 0}</dd></div>
                      </dl>
                      <p className="mt-2 font-medium">{manualPullReview.completeness === 'manual_pull_complete' ? (manualPullReview.archival_eligible ? 'Complete inventory · archival eligible' : 'Complete capture · review required before archival') : 'Partial inventory · archival disabled'}</p>
                      <p className="mt-1 text-slate-600">{manualPullReview.candidate_count || 0} total candidates; {manualPullReview.validated_count ?? (manualPullReview.candidates || []).filter(item => item.state === 'validated').length} validated and selected by default. You can select additional candidates below for manual review.</p>
                      <div className="mt-2 max-h-[56vh] space-y-2 overflow-y-auto pr-1" data-testid="manual-pull-candidate-list" aria-label="Pulled listing candidates">
                        {(manualPullReview.candidates || []).slice(0, 100).map(candidate => {
                          const parcel = (candidate.parcels || [])[0] || {};
                          const identity = parcel.address || parcel.apn || candidate.source_identifier || 'Identity unavailable';
                          const coordinates = parcel.latitude != null && parcel.longitude != null
                            ? `${parcel.latitude}, ${parcel.longitude}`
                            : '';
                          return (
                            <label key={candidate.candidate_id} data-testid="manual-pull-candidate-card" className="block cursor-pointer rounded-lg border border-slate-200 bg-white p-3 shadow-sm transition hover:border-indigo-300">
                              <div className="flex items-start gap-2">
                                <input type="checkbox" checked={manualPullSelected.has(candidate.candidate_id)} onChange={() => toggleManualPullCandidate(candidate.candidate_id)} className="mt-1" />
                                <span className="min-w-0 flex-1">
                                  <span className="flex items-start justify-between gap-2">
                                    <span className="font-medium text-indigo-950">{candidate.title || identity}</span>
                                    <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">{candidate.state || 'unresolved'}</span>
                                  </span>
                                  <span className="mt-1 block text-slate-700">{identity}</span>
                                  {candidate.property_type && <span className="mt-1 block text-slate-500">Type: {candidate.property_type}</span>}
                                  {candidate.listing_status && <span className="block text-slate-500">Status: {candidate.listing_status}</span>}
                                  {parcel.apn && <span className="block text-slate-500">APN: {parcel.apn}</span>}
                                  {coordinates && <span className="block text-slate-500">Coordinates: {coordinates}</span>}
                                  {candidate.canonical_url && <a href={candidate.canonical_url} target="_blank" rel="noreferrer" onClick={event => event.stopPropagation()} className="mt-1 block truncate text-indigo-700 underline">View source listing</a>}
                                </span>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                      <button type="button" onClick={importManualPullListings} disabled={saving || !manualPullSelected.size} className="mt-3 w-full rounded-lg bg-emerald-700 px-3 py-2 text-sm font-medium text-white disabled:opacity-50">Import selected listings ({manualPullSelected.size})</button>
                    </div>
                  )}
                </div>
                {manualPullMessage && <p role="status" className="rounded-lg bg-slate-50 p-3 text-xs text-slate-700">{manualPullMessage}</p>}
              </aside>
            </div>}
          </section>
        </div>}
      </main>
    </div>
  );
};

export default PortfolioPage;
